<!--
  Agenzia — creazione. Stessa impaginazione del dettaglio, così
  chi crea vede fin da subito la scheda che poi troverà.

  Tre scelte non ovvie:

  1) LE TARIFFE COMPENSO SONO NEL FORM, E RICHIESTE.
     A schema hanno `default: 0`: un'agenzia creata senza compilarle nasce con
     compensi a zero e i suoi driver risultano da pagare € 0,00 in COD6, senza
     che nulla segnali l'errore.

  2) NIENTE "Documento / P.IVA" né "Priorità": l'entity li marca DEPRECATO
     ("rimosso dai form", resi nullable apposta). Restano visibili nel
     dettaglio solo per le agenzie storiche che li hanno valorizzati.

  3) NIENTE sezione "Aree": le zone stanno sui driver come
     Regione→Provincia, non sull'agenzia.

  `/agency` è tutto sotto `@Roles(...GLOBAL_ROLES)`: la creazione è riservata
  a Simba, e un 403 va spiegato invece di comparire come errore generico.
-->
<template>
  <div class="aga">
    <div class="aga__head">
      <button
        type="button"
        class="aga__back"
        @click="goBack"
      >
        ‹
      </button>
      <div>
        <h1 class="aga__title">
          Nuova agenzia
        </h1>
        <p class="aga__sub">
          Anagrafica e tariffe compenso dei driver forniti.
        </p>
      </div>
      <div class="aga__sp" />
      <div class="aga__acts">
        <el-button @click="goBack">
          Annulla
        </el-button>
        <el-button
          type="primary"
          :loading="saving"
          @click="submit"
        >
          Crea agenzia
        </el-button>
      </div>
    </div>

    <el-form
      ref="formRef"
      :model="form"
      :rules="rules"
      label-position="top"
    >
      <div class="aga__grid">
        <div class="aga__col">
          <!-- --------------------------- DATI --------------------------- -->
          <section class="card">
            <div class="card__hd">
              <h3>Dati agenzia</h3>
            </div>
            <div class="card__bd">
              <div class="datagrid">
                <el-form-item
                  label="Ragione sociale"
                  prop="company_name"
                  class="datagrid__full"
                >
                  <el-input
                    v-model="form.company_name"
                    placeholder="Es. Milano Centro Srl"
                  />
                </el-form-item>

                <el-form-item
                  label="Nome referente"
                  prop="name"
                >
                  <el-input v-model="form.name" />
                </el-form-item>

                <el-form-item
                  label="Cognome referente"
                  prop="surname"
                >
                  <el-input v-model="form.surname" />
                </el-form-item>

                <el-form-item
                  label="Email"
                  prop="mail"
                >
                  <el-input
                    v-model="form.mail"
                    placeholder="referente@agenzia.it"
                  />
                </el-form-item>

                <el-form-item
                  label="Telefono"
                  prop="telephone_number"
                >
                  <el-input v-model="form.telephone_number" />
                </el-form-item>
              </div>
            </div>
          </section>

          <!-- --------------------- TARIFFE COMPENSI --------------------- -->
          <section class="card">
            <div class="card__hd">
              <h3>Tariffe compensi driver</h3>
              <span class="aga__sp" />
              <span class="card__note">alimentano il COD6</span>
            </div>
            <div class="card__bd">
              <div class="warnbox">
                <span class="warnbox__ico">⚠</span>
                <div class="warnbox__txt">
                  Se restano a zero, i driver di questa agenzia risulteranno da
                  pagare <b>€ 0,00</b> in contabilità.
                </div>
              </div>

              <div class="tar">
                <el-form-item
                  v-for="t in RATE_FIELDS"
                  :key="t.key"
                  :label="t.label"
                  :prop="t.key"
                >
                  <el-input
                    v-model.number="form[t.key]"
                    type="number"
                    step="0.01"
                    min="0"
                  >
                    <template
                      v-if="t.unit"
                      #append
                    >
                      {{ t.unit }}
                    </template>
                  </el-input>
                  <div class="tar__hint">
                    {{ t.hint }}
                  </div>
                </el-form-item>
              </div>
            </div>
          </section>
        </div>

        <!-- ============================ ASIDE ============================ -->
        <div class="aga__aside">
          <section class="card">
            <div class="card__hd">
              <h3>Riepilogo</h3>
            </div>
            <div class="card__bd">
              <div class="kv">
                <div class="kv__r">
                  <span class="kv__k">Agenzia</span>
                  <span class="kv__v">{{ form.company_name || '—' }}</span>
                </div>
                <div class="kv__r">
                  <span class="kv__k">Referente</span>
                  <span class="kv__v">{{ referente || '—' }}</span>
                </div>
                <div class="kv__r">
                  <span class="kv__k">Tariffa oraria</span>
                  <span class="kv__v">{{ euro(form.hourly_rate) }}/h</span>
                </div>
                <div class="kv__r">
                  <span class="kv__k">Rimborso km</span>
                  <span class="kv__v">{{ euro(form.km_refund_rate) }}/km</span>
                </div>
                <div class="kv__r">
                  <span class="kv__k">Compenso notturno</span>
                  <span class="kv__v">{{ euro(form.night_compensation_rate) }}</span>
                </div>
              </div>

              <p class="aga__note">
                I driver si collegano all'agenzia dalla scheda del singolo
                driver, dopo averla creata.
              </p>
            </div>
          </section>
        </div>
      </div>
    </el-form>
  </div>
</template>

<script>
import { ref, reactive, computed } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage } from 'element-plus';
import agencyAPI from '../../services/agencyAPI.js';

const RATE_FIELDS = [
  {
    key: 'hourly_rate', label: 'Tariffa oraria', unit: '€/h',
    hint: 'compenso base = ore × tariffa',
  },
  {
    key: 'km_refund_rate', label: 'Rimborso km jolly', unit: '€/km',
    hint: 'solo per il jolly',
  },
  {
    key: 'night_compensation_rate', label: 'Compenso notturno', unit: '€',
    hint: 'a servizio notturno',
  },
];

// I tre importi non possono essere negativi, e vanno chiesti esplicitamente:
// il default a zero renderebbe l'omissione invisibile.
const positiveRate = (label) => ({
  validator: (_rule, value, callback) => {
    if (value === '' || value === null || value === undefined) {
      return callback(new Error(`Inserire ${label.toLowerCase()}`));
    }
    if (Number(value) < 0) return callback(new Error('Non può essere negativo'));
    callback();
  },
  trigger: 'blur',
});

export default {
  name: 'AddAgencyPage',

  setup() {
    const router = useRouter();
    const formRef = ref(null);
    const saving = ref(false);

    const form = reactive({
      company_name: '',
      name: '',
      surname: '',
      mail: '',
      telephone_number: '',
      hourly_rate: null,
      km_refund_rate: null,
      night_compensation_rate: null,
    });

    const rules = {
      company_name: [
        { required: true, message: 'Inserire la ragione sociale', trigger: 'blur' },
      ],
      name: [{ required: true, message: 'Inserire il nome', trigger: 'blur' }],
      surname: [{ required: true, message: 'Inserire il cognome', trigger: 'blur' }],
      mail: [
        { required: true, message: 'Inserire la mail', trigger: 'blur' },
        { type: 'email', message: 'Indirizzo non valido', trigger: 'blur' },
      ],
      telephone_number: [
        { required: true, message: 'Inserire il telefono', trigger: 'blur' },
      ],
      hourly_rate: [positiveRate('la tariffa oraria')],
      km_refund_rate: [positiveRate('il rimborso km')],
      night_compensation_rate: [positiveRate('il compenso notturno')],
    };

    const referente = computed(() =>
      [form.name, form.surname].filter(Boolean).join(' ')
    );

    const euro = (n) =>
      n == null || n === '' ? '—' : `€ ${Number(n).toFixed(2).replace('.', ',')}`;

    const submit = async () => {
      if (!formRef.value) return;
      try {
        await formRef.value.validate();
      } catch {
        ElMessage.warning('Controlla i campi segnalati');
        return;
      }

      saving.value = true;
      try {
        const res = await agencyAPI.addAgency({ ...form });
        ElMessage.success('Agenzia creata');
        // Se il backend restituisce l'entità si va dritti alla sua scheda,
        // altrimenti si torna all'elenco.
        const id = res?.data?.id;
        router.push(id ? `/agency/${id}` : '/agency');
      } catch (e) {
        console.error('Creazione agenzia fallita:', e);
        const status = e?.response?.status;
        if (status === 403) {
          ElMessage.error('Le agenzie possono essere create solo da Simba');
        } else if (status === 409) {
          ElMessage.error('Esiste già un\'agenzia con questi dati');
        } else {
          ElMessage.error('Creazione non riuscita');
        }
      } finally {
        saving.value = false;
      }
    };

    const goBack = () => router.push('/agency');

    return { formRef, form, rules, saving, referente, RATE_FIELDS, euro, submit, goBack };
  },
};
</script>

<style scoped>
.aga { padding: 4px 0 32px; }
.aga__sp { flex: 1; }

.aga__head {
  display: flex; align-items: center; gap: 14px; flex-wrap: wrap; margin: 0 2px 18px;
}
.aga__back {
  width: 36px; height: 36px; flex: none; border-radius: 9px;
  border: 1px solid var(--border-color); background: var(--bg-primary);
  display: grid; place-items: center; cursor: pointer;
  font-size: 16px; color: var(--text-secondary); font-family: inherit;
}
.aga__back:hover { border-color: var(--brand-violet); }
.aga__title { margin: 0; font-size: 22px; color: var(--text-primary); }
.aga__sub { margin: 3px 0 0; font-size: 13px; color: var(--text-tertiary); }
.aga__acts { display: flex; gap: 8px; flex-wrap: wrap; }

.aga__grid {
  display: grid; grid-template-columns: 1fr 330px; gap: 16px; align-items: start;
}
@media (max-width: 960px) { .aga__grid { grid-template-columns: 1fr; } }
.aga__col { display: flex; flex-direction: column; gap: 16px; }
.aga__aside {
  position: sticky; top: 16px; display: flex; flex-direction: column; gap: 16px;
}
@media (max-width: 960px) { .aga__aside { position: static; } }

.card {
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
}
.card__hd {
  display: flex; align-items: center; gap: 10px;
  padding: 13px 16px; border-bottom: 1px solid var(--border-color);
}
.card__hd h3 { margin: 0; font-size: 14px; color: var(--text-primary); }
.card__note { font-size: 12px; color: var(--text-tertiary); }
.card__bd { padding: 16px; }

.datagrid { display: grid; grid-template-columns: 1fr 1fr; gap: 0 22px; }
@media (max-width: 560px) { .datagrid { grid-template-columns: 1fr; } }
.datagrid__full { grid-column: 1 / -1; }

.tar { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0 12px; }
@media (max-width: 700px) { .tar { grid-template-columns: 1fr; } }
.tar__hint {
  font-size: 11px; color: var(--text-tertiary); line-height: 1.4; margin-top: 2px;
}

.warnbox {
  display: flex; align-items: flex-start; gap: 10px;
  padding: 10px 13px; margin-bottom: 14px;
  border-radius: 9px; font-size: 12.5px; line-height: 1.5;
  color: var(--state-attn); background: var(--state-attn-bg);
  border: 1px solid var(--state-attn-line);
}
.warnbox__ico { flex: none; font-size: 14px; line-height: 1.35; }
.warnbox__txt { flex: 1; }

.kv { display: flex; flex-direction: column; }
.kv__r {
  display: flex; justify-content: space-between; align-items: center;
  gap: 12px; padding: 9px 0; border-bottom: 1px solid var(--border-color);
}
.kv__r:last-child { border-bottom: 0; }
.kv__k { color: var(--text-tertiary); font-size: 12.5px; flex: none; }
.kv__v {
  font-weight: 600; text-align: right; color: var(--text-primary);
  font-variant-numeric: tabular-nums;
}
.aga__note {
  margin: 12px 0 0; font-size: 12px; color: var(--text-tertiary); line-height: 1.5;
}

/* le etichette dei form dentro le card seguono lo stile del dettaglio */
.aga :deep(.el-form-item__label) {
  font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.05em;
  color: var(--text-tertiary); font-weight: 700; padding-bottom: 3px;
}
</style>
